import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiFetch, setSessionToken, clearSessionToken } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/auth/me`, {
                credentials: 'include'
            });

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
            } else {
                clearSessionToken();
                setUser(null);
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = () => {
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const logout = async () => {
        try {
            await apiFetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            clearSessionToken();
            setUser(null);
            window.location.href = '/';
        }
    };

    const exchangeSession = async (sessionId) => {
        try {
            const response = await apiFetch(`${API_URL}/api/auth/session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ session_id: sessionId })
            });

            if (!response.ok) {
                throw new Error('Session exchange failed');
            }

            const authData = await response.json();
            if (!authData.session_token || !authData.user) {
                throw new Error('Session exchange returned incomplete auth data');
            }

            setSessionToken(authData.session_token);
            setUser(authData.user);
            return authData.user;
        } catch (error) {
            clearSessionToken();
            console.error('Session exchange failed:', error);
            throw error;
        }
    };

    const value = {
        user,
        loading,
        login,
        logout,
        exchangeSession,
        checkAuth,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};